import type { en } from './en'

/**
 * French catalog. AI-drafted translations — pending human review.
 * Must contain exactly the same keys as ./en.ts (enforced by a unit test).
 */
type Messages = { [K in keyof typeof en]: Record<keyof (typeof en)[K], string> }

export const fr: Messages = {
  newsletter: {
    byAuthor: 'Par {author}',
    readMore: 'Lire la suite →',
    rssPreviewPlaceholder: 'Aucun élément pour le moment — saisissez une URL de flux RSS pour voir les éléments.',
    managePreferences: 'Gérer les préférences',
    unsubscribe: 'Se désabonner',
  },
  email: {
    poweredBy: 'Propulsé par PubGen.AI',
    subscribedToNewsletterTitle: 'Abonnement à l’infolettre confirmé',
    subscribedToNewslettersTitle: 'Abonnement aux infolettres confirmé',
    subscribedToNewsletterBody: 'Vous êtes maintenant abonné à {newsletterName}.',
    subscribedToNewslettersIntro: 'Vous êtes maintenant abonné aux infolettres suivantes :',
    subscribedToNewslettersText: 'Vous êtes maintenant abonné à : {newsletterNames}.',
    willReceiveUpdatesAt: 'Vous recevrez des nouvelles à l’adresse {email}.',
    subscriptionSuccessTitle: 'Abonnement réussi',
    subscriptionSuccessBody: 'Votre abonnement a été traité avec succès.',
    subscriptionSuccessBodyWithProduct: 'Votre abonnement à {product} a été traité avec succès.',
    subscriptionCancelledTitle: 'Abonnement annulé',
    subscriptionCancelledBody: 'Votre abonnement a été annulé.',
    subscriptionCancelledBodyWithProduct: 'Votre abonnement « {product} » a été annulé.',
    willNoLongerReceiveUpdatesAt: 'Vous ne recevrez plus de nouvelles à l’adresse {email}.',
    signInTitle: 'Connexion à {siteName}',
    signInMagicLinkBody:
      'Veuillez vous connecter en cliquant sur le lien ci-dessous. Ce lien restera valide pendant 24 heures.',
    signInCodeBody:
      'Veuillez vous connecter en utilisant le code ci-dessous. Ce code restera valide pendant 24 heures.',
    signInInAppHint:
      'Vous vous connectez depuis l’application Gmail ou Facebook ? Pas d’inquiétude — vous devrez peut-être simplement vous reconnecter une fois dans votre navigateur.',
    signInCta: 'Se connecter',
    signInMagicLinkText:
      'Veuillez vous connecter en cliquant sur le lien ci-dessous. Ce lien restera valide pendant 24 heures sur {siteName} : {magicLink}.\nSi vous n’avez pas demandé cette connexion, vous pouvez ignorer ce courriel.',
    signInCodeText:
      'Veuillez vous connecter en utilisant le code ci-dessous. Ce code restera valide pendant 24 heures sur {siteName} : {code}.\nSi vous n’avez pas demandé cette connexion, vous pouvez ignorer ce courriel.',
    donationTitle: 'Merci pour votre don',
    donationBody: 'Votre don à {brandName} a été traité avec succès.',
    donationContact: 'Pour toute question, veuillez nous contacter à {contactUrl}.',
    giftCodeTitle: 'Code cadeau d’abonnement',
    giftCodeBody:
      'Vous avez acheté un abonnement cadeau. Envoyez le lien suivant au destinataire pour qu’il puisse profiter de son cadeau.',
    preferencesSubject: 'Gérez vos préférences de courriel pour {siteName}',
    preferencesTitle: 'Gérez vos préférences de courriel',
    preferencesBody:
      'Utilisez le lien ci-dessous pour choisir les courriels de {siteName} que vous recevez, ou pour vous désabonner.',
    preferencesCta: 'Gérer les préférences',
    printOrderConfirmedTitle: 'Votre commande d’impression est confirmée',
    printOrderConfirmedBody: 'Merci pour votre commande ! Votre {quantity} x {productType} est en préparation.',
    orderReference: 'Référence de commande : {ref}',
    printOrderTrackCta: 'Suivre votre commande',
    printOrderTrackAt: 'Suivez votre commande à {url}',
    printOrderTrackingTitle: 'Informations de suivi de votre commande',
    printOrderTrackingBody: 'Votre commande a été expédiée. Numéro de suivi : {trackingNumber}.',
    printOrderViewShipmentCta: 'Voir l’expédition',
    printOrderShippedTitle: 'Votre commande d’impression est en route',
    printOrderShippedBody: 'Votre commande est en transit.',
    printOrderShippedTracking: 'Suivi : {trackingNumber}.',
    printOrderDeliveredTitle: 'Votre commande d’impression a été livrée',
    printOrderDeliveredBody: 'Votre commande a été livrée. Nous espérons qu’elle vous plaira.',
    printOrderDetailsCta: 'Détails de la commande',
    printOrderDetailsAt: 'Détails à {url}',
    printOrderCancelledTitle: 'Votre commande d’impression a été annulée',
    printOrderCancelledBody:
      'Votre commande a été annulée. Si vous ne vous y attendiez pas, n’hésitez pas à nous contacter.',
    customerServiceEmailOnly: 'Service à la clientèle : écrivez à {email}.',
    customerServicePhoneOnly: 'Service à la clientèle : appelez le {phone}.',
    customerServiceBoth: 'Service à la clientèle : écrivez à {email} ou appelez le {phone}.',
  },
  errors: {
    generic: 'Une erreur est survenue, veuillez réessayer plus tard.',
    siteNotConnectedToStripe: 'Ce site n’est pas connecté à un compte de paiement.',
    subscriptionPlanNotFound: 'Forfait d’abonnement introuvable.',
    subscriptionAlreadyExists: 'Un abonnement avec cette adresse courriel existe déjà.',
    printOnDemandNotConfigured: 'L’impression à la demande n’est pas configurée pour ce site.',
    printOrderNotFound: 'Commande introuvable.',
    newsletterNotAvailable: 'Une ou plusieurs infolettres ne sont pas disponibles pour l’abonnement.',
    newsletterVerificationFailed: 'La vérification a échoué. Veuillez réessayer.',
    newsletterSubscribeFailed: 'Nous n’avons pas pu vous abonner pour le moment. Veuillez réessayer plus tard.',
  },
  errorTypes: {
    generic: 'Une erreur est survenue, veuillez réessayer plus tard.',
    genericWithCode: 'Une erreur est survenue, veuillez réessayer plus tard. (Code d’erreur : {errorType}) ',
    AlreadySubscribed: 'Un abonné avec cette adresse courriel existe déjà pour cette publication.',
    CreateSubscriptionError:
      'Une erreur est survenue lors du traitement de l’abonnement. Veuillez réessayer plus tard.',
    CancelSubscriptionError:
      'Une erreur est survenue lors de l’annulation de l’abonnement. Veuillez réessayer plus tard.',
    ListSubscriptionError:
      'Une erreur est survenue lors de la récupération des abonnements. Veuillez réessayer plus tard.',
    Unauthorized: 'Vous n’êtes pas autorisé à effectuer cette action.',
    HeadlineGeneration: 'Une erreur est survenue lors de la génération du titre, veuillez réessayer plus tard.',
    NoArticle: 'L’article n’a pas pu être téléchargé. Veuillez réessayer plus tard.',
    DomainFormatError: 'Le format du domaine est incorrect. Veuillez utiliser un format de domaine valide.',
    DomainUnavailable: 'Ce domaine est déjà utilisé. Veuillez utiliser un autre domaine.',
    DomainNotConnected: 'Le domaine n’est pas connecté au site. Veuillez connecter le domaine au site.',
    NoDomainConnected: 'Aucun domaine n’est connecté au site. Veuillez connecter un domaine au site.',
    NewsLetterNotFound: 'L’infolettre est introuvable. Veuillez réessayer plus tard.',
    WrongSlugFormat:
      'Le format du slug est incorrect. Veuillez utiliser un slug qui commence et se termine par une barre oblique.',
    SlugAlreadyExists:
      'Une page avec ce slug existe déjà. Si vous créez une nouvelle page, veuillez modifier le titre de la page, sinon choisissez un autre slug.',
    NameAlreadyExists:
      'Une rubrique, une étiquette ou un auteur portant ce nom existe déjà sur ce site. Veuillez choisir un autre nom.',
    StripeNotConnected: 'Le compte Stripe n’est pas connecté. Veuillez connecter le compte Stripe au site.',
    SubdomainWrongFormat:
      'Le format du sous-domaine est incorrect. Veuillez utiliser un format de sous-domaine valide.',
    DomainNotFound: 'Le domaine est introuvable. Veuillez réessayer plus tard.',
    WorkspaceNotFound: 'L’espace de travail est introuvable. Veuillez réessayer plus tard.',
    UserNotCreated: 'L’utilisateur n’a pas pu être créé. Veuillez réessayer plus tard.',
    WorkspaceHasActiveBrands:
      'Vous ne pouvez pas supprimer un espace de travail qui contient des marques actives. Veuillez supprimer toutes les marques avant de supprimer l’espace de travail.',
    DomainAlreadyConnected:
      'Ce site a déjà un domaine connecté. Veuillez déconnecter le domaine actuel avant d’en connecter un nouveau.',
    UserAlreadyExists:
      'Un utilisateur avec cette adresse courriel existe déjà dans l’espace de travail. Veuillez utiliser une autre adresse courriel.',
    NewsLetterAlreadyExists: 'Une infolettre portant ce nom existe déjà. Veuillez choisir un autre nom.',
    StripeCheckoutError: 'Une erreur est survenue lors du traitement du paiement. Veuillez réessayer plus tard.',
    NoSubscriptionsToExport: 'Il n’y a aucun abonnement à exporter pour cette période.',
    InvalidSiteId: 'L’identifiant du site est invalide.',
    ExportMultipleBrandError: 'Toutes les pages doivent appartenir à la même marque.',
    ExportNonArticlePageError: 'Seules les pages d’article peuvent être exportées.',
    ExportingData:
      'L’exportation des données prendra quelques minutes. Veuillez vérifier votre courriel pour le lien de téléchargement.',
    StripeDonationProductAlreadyExists:
      'Ce site a déjà un produit de don. Veuillez supprimer le produit de don existant avant d’en créer un nouveau.',
    SubscriptionPlanInUse:
      'Ce forfait est proposé pour le changement de forfait dans le portail client. Retirez-le de la configuration du portail avant de le supprimer.',
  },
  chatbot: {
    defaultGreeting: 'Bonjour, comment puis-je vous aider aujourd’hui ?',
  },
  seo: {
    sectionTitle: 'Les dernières nouvelles, histoires et articles {sectionName} | {siteName}.',
    sectionDescription:
      'Les dernières nouvelles, histoires et articles {sectionName} | {siteName}. Restez informé grâce à une couverture approfondie de {sectionName} et plus encore. Trouvez des histoires captivantes, des analyses pertinentes et des nouvelles de dernière heure en un seul endroit.',
    tagTitle: 'Découvrez les nouvelles, histoires et articles sur {tagName} | {siteName}.',
    tagDescription:
      'Découvrez une collection d’articles liés à {tagName} sur {siteName}. Restez informé des dernières nouvelles, analyses et perspectives. Explorez des points de vue variés et une couverture approfondie de {tagName} en un seul endroit.',
    authorTitle: 'Articles, histoires et nouvelles par {authorName} | {siteName}.',
    authorDescription:
      'Découvrez les articles et contributions de {authorName} sur {siteName}. Des histoires captivantes, des analyses expertes et des commentaires stimulants de cet auteur accompli. Apprenez-en plus sur le parcours et l’expertise de {authorName} dans sa biographie.',
    homepageDescription: 'Les dernières nouvelles, histoires et articles de {siteName}.',
    latestNewsFeedTitle: '{siteName} — Dernières nouvelles',
    feedDescription: 'Contenu de {domainName}',
    notFoundTitle: 'Page introuvable | {siteName}',
    notFoundDescription: 'La page que vous cherchez n’existe pas.',
    accountTitle: 'Mon compte | {siteName}',
    accountDescription:
      'Connectez-vous à votre compte {siteName} · Gérer mon compte · Gérer vos abonnements · Mon compte · Livraison à domicile et abonnement numérique · Ma liste de lecture',
    searchTitle: 'Recherche | {siteName}',
    searchDescription: 'Page de recherche. Utilisez la boîte de recherche pour trouver ce que vous cherchez.',
    authorFallbackName: 'Nom de l’auteur',
    checkoutTitle: 'Paiement | {siteName}',
    checkoutDescription: 'Processus de paiement de l’abonnement | {siteName}',
    latestArticlesTitle: 'Dernières nouvelles | {siteName}',
    latestArticlesDescription: '{siteName} - Dernières nouvelles de dernière heure.',
    paymentsTitle: 'Paiements | {siteName}',
    paymentsDescription: 'Paiements pour {siteName}.',
    privacyPolicyTitle: 'Politique de confidentialité | {siteName}',
    privacyPolicyDescription: 'Politique de confidentialité | {siteName}.',
    signInTitle: 'Connexion | {siteName}',
    signInDescription: 'Connexion | {siteName} - Profitez de votre abonnement en ligne dès aujourd’hui.',
    thankYouTitle: 'Connexion | {siteName}',
    thankYouDescription: 'Merci pour votre abonnement ! ♥ | {siteName}',
    subscribeTitle: 'Abonnez-vous | {siteName}',
    subscribeDescription: 'Abonnez-vous dès aujourd’hui | {siteName}.',
    termsTitle: 'Conditions d’utilisation | {siteName}',
    termsDescription: 'Conditions de service/d’utilisation/de vente | {siteName}.',
    contactUsTitle: 'Contactez-nous | {siteName}',
    contactUsDescription: 'Communiquez avec nous. | {siteName}',
    spotlightDefaultName: 'En vedette',
    spotlightTitle: '{spotlightName} | {siteName}',
    spotlightDescription: 'Histoires en vedette et faits saillants. | {siteName}',
    eventsTitle: 'Événements | {siteName}',
    eventsDescription: 'Événements à venir. | {siteName}',
    allTagsTitle: 'Toutes les étiquettes | {siteName}',
    allTagsDescription: 'Parcourez toutes les étiquettes. | {siteName}',
    allSectionsTitle: 'Toutes les rubriques | {siteName}',
    allSectionsDescription: 'Parcourez toutes les rubriques. | {siteName}',
    allAuthorsTitle: 'Tous les auteurs | {siteName}',
    allAuthorsDescription: 'Découvrez nos journalistes et collaborateurs. | {siteName}',
    newslettersTitle: 'Infolettres | {siteName}',
    newslettersDescription: 'Abonnez-vous à nos infolettres. | {siteName}',
    sitemapTitle: 'Plan du site | {siteName}',
    sitemapDescription: 'Plan du site et navigation. | {siteName}',
    giftTitle: 'Abonnement cadeau | {siteName}',
    giftDescription: 'Offrez un abonnement en cadeau. | {siteName}',
    giftRedeemTitle: 'Utiliser un cadeau | {siteName}',
    giftRedeemDescription: 'Utilisez votre abonnement cadeau. | {siteName}',
  },
} as const
